import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Form } from 'antd';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import styled from 'styled-components';
import storeApis from 'modules/store/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import { setPageLoading } from 'widgets/PageLoading';
import BaseInput from 'basic-ui-lib/components/Input/BaseInput';
import { useMediaQuery } from 'react-responsive';
import { Colors } from 'basic-ui-lib';

export type ResetPasswordModalRef = {
  open: (storeId: string) => void;
  close: () => void;
};

type Props = {
  onSuccess?: () => void;
};

interface FormValues {
  storeId: string;
  password: string;
}

export const useResetPasswordModalRef = () => useRef<ResetPasswordModalRef>(null);

const ResetPasswordModal = forwardRef<ResetPasswordModalRef, Props>(({ onSuccess }, ref) => {
  const [form] = Form.useForm<FormValues>();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useImperativeHandle(ref, () => ({
    open: (storeId: string) => { 
      form.setFieldsValue({ storeId }); 
      setIsOpen(true); 
    },
    close: () => setIsOpen(false),
  }));

  const onClose = () => {
    setIsOpen(false);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async (values: FormValues) => {
    try {
      setPageLoading(true);
      const res = await storeApis.resetPassword({
        storeId: values.storeId,
        password: values.password,
      });
      if (res?.data?.data) {
        toast.success('Reset password successfully');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to reset store password');
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <BaseModal
      title='Reset Password'
      isOpen={isOpen}
      width={600}
      onClose={onClose}
      okText='OK'
      onOk={handleSubmit}
      isMobile={isMobile}
    >
      <Container>
        <Form layout='vertical' form={form} onFinish={handleFinish}>
          <Form.Item name='storeId' hidden />
          <Form.Item
              name='password'
              label='New Password'
              rules={[{ required: true, message: 'Password is required' }]}
            >
            <BaseInput placeholder='New Password' autoFocus type='password' />
          </Form.Item>
        </Form>
      </Container>
    </BaseModal>
  );
});

ResetPasswordModal.displayName = 'ResetPasswordModal';
export default ResetPasswordModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 0 50px 20px 50px;

  @media (max-width: 768px) {
    padding: 16px 16px 32px 16px;
    .ant-form-item {
      margin-bottom: 0;
    }

    background: ${Colors.neutral.bg[2]};
  }
`;

